import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The density-varying spacing tokens (VISUAL_SYSTEM_PLAN U2.1), as
 * tailwind-merge sees them: the class part that follows `p-`/`py-`/….
 *
 * They are `theme.extend` keys whose values are bare `var()` entries
 * (`tailwind.config.js`), because a value that has to change with
 * `[data-density]` cannot be resolved in a config file evaluated once at build
 * time. That naming IS the feature.
 *
 * Exported for ONE consumer: `utils.test.ts` holds this list against the keys
 * `tailwind.config.js` actually declares, in both directions. Two hand-kept
 * copies of a token list is precisely how `cn()` silently stops knowing about
 * the fourth token somebody adds; nothing else should import it.
 */
export const DENSITY_PADDING = ["gutter", "card", "row-y"] as const;

/**
 * Every padding group tailwind-merge knows, including the logical-property
 * spellings. The three tokens are taught to all of them rather than to the
 * four in use today (`p-card`, `p-gutter`, `px-gutter`, `py-row-y`): a group
 * left out is not a smaller surface, it is the same trap re-armed for whoever
 * first writes `pt-card`.
 */
const PADDING_GROUPS = ["p", "px", "py", "ps", "pe", "pt", "pr", "pb", "pl"] as const;

/**
 * `twMerge`, taught the three named padding keys.
 *
 * Stock tailwind-merge decides the `p-*` group by VALIDATING the utility's
 * value — it recognises a length (`p-4`) and an arbitrary value (`p-[3px]`),
 * but a bare theme key it has never heard of is not either of those, so it
 * cannot tell that `p-card` and `p-4` are the same declaration. Measured on
 * 2.5.4 before this config existed (flowstack#1044):
 *
 *     p-card   + p-4    => "p-card p-4"     <- both survive
 *     py-row-y + py-2   => "py-row-y py-2"  <- both survive
 *     p-5      + p-4    => "p-4"            <- control: a stock pair collapses
 *
 * Both classes reached the DOM and the winner was decided by generated-CSS
 * order rather than by call-site order — which made `ui/card.tsx`'s documented
 * escape hatch ("a caller that needs a different inset passes it through
 * `className`, `cn()` merges it") false as written. Nothing rendered wrong,
 * because no call site passed a conflicting literal; it was a trap that would
 * spring the first time one did.
 *
 * `extend` (not `override`) is deliberate: it APPENDS these keys to the
 * validators already in each group, so `p-4`, `p-[3px]` and `p-px` keep
 * resolving exactly as they did. The conflict map is untouched too, so the
 * axis rules stay stock — `p-card pt-0` and `px-3 py-row-y` both keep both
 * classes, which is what `card.tsx` and `table.tsx` already ship.
 *
 * Configuring the tool rather than renaming the tokens is the point: the
 * alternative — turning them back into stock scale entries — would delete
 * the density variance that is the whole reason they are named.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: Object.fromEntries(
      PADDING_GROUPS.map((group) => [group, [{ [group]: [...DENSITY_PADDING] }]])
    ),
  },
});

/**
 * The app's own class merger, for its UNPREFIXED Tailwind classes.
 *
 * It is not the Kit's `cn`, which merges only `fsui-` classes: a class passed
 * through `className` to a Kit primitive stays on the element beside the Kit's
 * own, and wins at equal specificity, by source order (`main.tsx` loads
 * `globals.css` after the Kit's `styles.css`). A Kit variant or state selector
 * of higher specificity (`fsui-hover:…`, `fsui-data-[state=open]:…`) still
 * wins over a plain host utility.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
